import { motion, AnimatePresence } from 'framer-motion';

interface FABAction {
  id: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  color?: string;
  bgColor?: string;
  disabled?: boolean;
}

interface ExpandableFABProps {
  actions: FABAction[];
  position: 'left' | 'right';
  isExpanded: boolean;
  onActionClick?: () => void;
  /** Stack actions in a row (for top chrome toolbar). */
  layout?: 'vertical' | 'horizontal';
  /** Vertical stack animation — down keeps actions below the profile chip in top chrome. */
  expandDirection?: 'up' | 'down';
}

export function ExpandableFAB({
  actions,
  position,
  isExpanded,
  onActionClick,
  layout = 'vertical',
  expandDirection = 'up',
}: ExpandableFABProps) {
  // Animation variants for the action buttons
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.05,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        staggerChildren: 0.03,
        staggerDirection: -1,
      },
    },
  };

  const slideOffset = expandDirection === 'down' ? -10 : 10;

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: layout === 'horizontal' ? 0 : slideOffset,
      x: layout === 'horizontal' ? -8 : 0,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25,
      },
    },
    exit: {
      opacity: 0,
      y: layout === 'horizontal' ? 0 : slideOffset,
      x: layout === 'horizontal' ? -8 : 0,
      scale: 0.8,
      transition: {
        duration: 0.15,
      },
    },
  };

  const actionButtonClass =
    'w-11 h-11 md:w-10 md:h-10 shrink-0 rounded-xl shadow-lg dark:shadow-black/40 flex items-center justify-center transition-colors disabled:opacity-45 disabled:pointer-events-none disabled:cursor-not-allowed';

  return (
    <div
      className={
        layout === 'horizontal'
          ? 'flex flex-row flex-wrap items-center gap-2 min-w-0'
          : `flex flex-col gap-2 ${position === 'right' ? 'items-end' : 'items-start'}`
      }
    >
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={layout === 'horizontal' ? 'flex flex-row flex-wrap items-center gap-2' : 'flex flex-col gap-2'}
          >
            {actions.map((action) => (
              <motion.button
                key={action.id}
                data-tour-target={action.id}
                variants={itemVariants}
                disabled={action.disabled}
                onClick={() => {
                  if (action.disabled) return;
                  action.onClick();
                  onActionClick?.();
                }}
                className={`${actionButtonClass} ${action.bgColor || 'bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl dark:ring-1 dark:ring-gray-700/50'} ${action.color || 'text-gray-600 dark:text-gray-300'} ${!action.bgColor ? 'hover:bg-gray-50 dark:hover:bg-gray-800/80' : ''}`}
                whileHover={{ scale: action.disabled ? 1 : 1.05 }}
                whileTap={{ scale: action.disabled ? 1 : 0.95 }}
                aria-label={action.label}
                title={action.label}
              >
                {action.icon}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
